/**
 * Telegram-Markdown → renderable blocks.
 *
 * The research bot formats every reply for Telegram's legacy Markdown:
 * *bold*, _italic_, `code`, ```fences```, [text](url), backslash escapes —
 * and, because Telegram has no tables, it draws them as runs of aligned
 * monospace lines (`factor   ours   FactorAlpha`). The website shows the
 * same reply text (so the two interfaces can never disagree), but a browser
 * can do better than monospace: this module recognises those aligned runs
 * and fenced dataframes and turns them into real tables, and rewrites the
 * inline markup into the CommonMark that react-markdown understands.
 *
 * parseTelegram(text) → [
 *   { type: "md",    lines: ["**bold** line", "", "next"] },
 *   { type: "table", header: ["factor","ours","FactorAlpha"], rows: [[...]], align: ["left","right",...] },
 *   { type: "code",  text: "raw fenced text" },
 * ]
 */

const FENCE = "```";

/* ── inline: Telegram legacy → CommonMark ─────────────────────────────── */

const isWordChar = (ch) => /[A-Za-z0-9]/.test(ch || "");

/** Convert one line of Telegram inline markup to CommonMark. */
export function inlineToMarkdown(line) {
    let out = "";
    let i = 0;
    const n = line.length;

    while (i < n) {
        const ch = line[i];

        // Backslash escapes: Telegram's escape() protects _ * ` [ — emit the
        // literal, escaped for CommonMark so it stays literal there too.
        if (ch === "\\" && i + 1 < n) {
            const nxt = line[i + 1];
            out += /[_*`\[\]\\]/.test(nxt) ? `\\${nxt}` : nxt;
            i += 2;
            continue;
        }

        // Code span: copy verbatim (both dialects agree on backticks).
        if (ch === "`") {
            const close = line.indexOf("`", i + 1);
            if (close === -1) { out += "\\`"; i += 1; continue; }
            out += line.slice(i, close + 1);
            i = close + 1;
            continue;
        }

        // Link: [text](url) — same syntax, but the text may hold Telegram bold.
        if (ch === "[") {
            const m = /^\[([^\]]*)\]\(([^)\s]+)\)/.exec(line.slice(i));
            if (m) {
                out += `[${inlineToMarkdown(m[1])}](${m[2]})`;
                i += m[0].length;
                continue;
            }
            out += "\\[";
            i += 1;
            continue;
        }

        // Bold: *text* → **text**. The model sometimes writes CommonMark
        // **text** directly; accept that too rather than escaping the extras.
        if (ch === "*" && line[i + 1] === "*") {
            const close = line.indexOf("**", i + 2);
            if (close !== -1 && close > i + 2) {
                out += `**${inlineToMarkdown(line.slice(i + 2, close))}**`;
                i = close + 2;
                continue;
            }
            out += "\\*\\*";
            i += 2;
            continue;
        }
        if (ch === "*") {
            const close = findClose(line, i, "*");
            if (close !== -1) {
                out += `**${inlineToMarkdown(line.slice(i + 1, close))}**`;
                i = close + 1;
                continue;
            }
            out += "\\*";
            i += 1;
            continue;
        }

        // Italic: _text_ → *text*. Underscores inside words (snake_case) are
        // literal in Telegram too, so only an underscore at a word boundary opens.
        if (ch === "_") {
            const prev = i > 0 ? line[i - 1] : "";
            if (!isWordChar(prev)) {
                const close = findClose(line, i, "_");
                if (close !== -1 && !isWordChar(line[close + 1] || "")) {
                    out += `*${inlineToMarkdown(line.slice(i + 1, close))}*`;
                    i = close + 1;
                    continue;
                }
            }
            out += "\\_";
            i += 1;
            continue;
        }

        // Characters CommonMark would otherwise read as markup.
        if (ch === "#" && out.trim() === "") { out += "\\#"; i += 1; continue; }
        // "<ticker>" would be read as raw HTML and dropped by the renderer.
        if (ch === "~" || ch === ">" || ch === "<") { out += `\\${ch}`; i += 1; continue; }

        out += ch;
        i += 1;
    }
    return out;
}

/** Index of the closing marker for the one at `start`, skipping escapes and code spans; -1 if none. */
function findClose(line, start, marker) {
    let j = start + 1;
    while (j < line.length) {
        const c = line[j];
        if (c === "\\") { j += 2; continue; }
        if (c === "`") {
            const close = line.indexOf("`", j + 1);
            if (close === -1) return -1;
            j = close + 1;
            continue;
        }
        if (c === marker) return j === start + 1 ? -1 : j;
        j += 1;
    }
    return -1;
}

/* ── tables from aligned monospace ────────────────────────────────────── */

const NUMERIC = /^[-+−–]?\$?\(?[\d,]*\.?\d+%?\)?[kMBTx]?$|^[-–—]$|^n\/a$/i;

/**
 * Split fixed-width rows into cells using the columns that are blank in
 * every row as separators. Right-aligned numbers separated by one space
 * still split correctly, and a two-word label ("Op margin") survives because
 * its inner space is not blank in the rows above and below.
 */
function splitFixedWidth(rows) {
    const width = Math.max(...rows.map((r) => r.length));
    const padded = rows.map((r) => r.padEnd(width));
    const blank = [];
    for (let c = 0; c < width; c += 1) blank.push(padded.every((r) => r[c] === " "));

    const spans = [];
    let start = null;
    for (let c = 0; c <= width; c += 1) {
        const isBlank = c === width ? true : blank[c];
        if (!isBlank && start === null) start = c;
        if (isBlank && start !== null) { spans.push([start, c]); start = null; }
    }
    if (spans.length < 2) return null;

    let cells = padded.map((r) => spans.map(([a, b]) => r.slice(a, b).trim()));
    // Drop columns that are empty everywhere (artefacts of a wide gap).
    const keep = spans.map((_, k) => cells.some((row) => row[k] !== ""));
    cells = cells.map((row) => row.filter((_, k) => keep[k]));

    // A two-word header over a one-word column ("Dividends Paid" above
    // "-$5.06B") splits into two cells while every body row has one. When the
    // header alone has more cells than the body, re-slice it using the body's
    // own column boundaries: each body column claims the header text between
    // the midpoints of the gaps either side of it.
    const bodyWidths = cells.slice(1).map((r) => r.filter((c) => c !== "").length);
    const bodyCols = bodyWidths.length ? Math.max(...bodyWidths) : 0;
    if (cells.length > 1 && bodyCols >= 2 && cells[0].filter((c) => c !== "").length > bodyCols) {
        const body = padded.slice(1);
        const bBlank = [];
        for (let c = 0; c < width; c += 1) bBlank.push(body.every((r) => r[c] === " "));
        const bSpans = [];
        let s = null;
        for (let c = 0; c <= width; c += 1) {
            const isBlank = c === width ? true : bBlank[c];
            if (!isBlank && s === null) s = c;
            if (isBlank && s !== null) { bSpans.push([s, c]); s = null; }
        }
        if (bSpans.length >= 2) {
            // Each header word joins the body column nearest to it (distance
            // zero when they overlap), so "Dividends Paid" stays one cell.
            const headerWords = [];
            const re = /\S+/g;
            let m;
            while ((m = re.exec(padded[0])) !== null) headerWords.push([m.index, m.index + m[0].length, m[0]]);
            const buckets = bSpans.map(() => []);
            headerWords.forEach(([wa, wb, w]) => {
                let best = 0;
                let bestD = Infinity;
                bSpans.forEach(([a, b], k) => {
                    const d = wb <= a ? a - wb : wa >= b ? wa - b : 0;
                    if (d < bestD) { bestD = d; best = k; }
                });
                buckets[best].push(w);
            });
            const headerCells = buckets.map((ws) => ws.join(" "));
            const bodyCells = body.map((r) => bSpans.map(([a, b]) => r.slice(a, b).trim()));
            return [headerCells, ...bodyCells];
        }
    }
    return cells;
}

/** A table block from raw rows, or null when they do not look tabular. */
function tableFromRows(rows, trailing = []) {
    const clean = rows.map((r) => r.replace(/\t/g, "    ").replace(/\s+$/, ""));
    if (clean.length < 2) return null;
    let cells = splitFixedWidth(clean);
    if (!cells) return null;
    // Anything after the closing backtick on a row (the fm "(+1)" gap note)
    // becomes one more column.
    if (trailing.some((t) => t)) cells = cells.map((row, k) => [...row, (trailing[k] || "").trim()]);

    const widths = cells.map((r) => r.length);
    const target = widths[0];
    if (target < 2) return null;
    // Rows may lose a trailing empty cell; pad rather than reject.
    const norm = cells.map((r) => (r.length < target ? [...r, ...Array(target - r.length).fill("")] : r.slice(0, target)));
    if (norm.some((r) => r.length !== target)) return null;

    const [header, ...body] = norm;
    const align = header.map((_, k) => {
        const vals = body.map((r) => r[k]).filter((v) => v !== "");
        return vals.length && vals.every((v) => NUMERIC.test(v)) ? "right" : "left";
    });
    return { type: "table", header: header.map(stripInline), rows: body.map((r) => r.map(stripInline)), align };
}

/** Cell text is plain: strip Telegram escapes and stray markers. */
function stripInline(s) {
    return s.replace(/\\([_*`\[])/g, "$1").replace(/^\*(.*)\*$/, "$1").replace(/^_(.*)_$/, "$1");
}

/** `code`  optional-trailing-text  — the shape of the bot's aligned rows. */
const MONO_ROW = /^`([^`]*)`(.*)$/;

/* ── blocks ────────────────────────────────────────────────────────────── */

export function parseTelegram(text) {
    const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
    const blocks = [];
    let md = [];

    const flushMd = () => {
        while (md.length && md[md.length - 1] === "") md.pop();
        while (md.length && md[0] === "") md.shift();
        if (md.length) blocks.push({ type: "md", lines: md });
        md = [];
    };

    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        // Fenced block: a dataframe (to_string) becomes a table, anything else code.
        if (line.trim().startsWith(FENCE)) {
            const first = line.trim().slice(FENCE.length);
            const body = [];
            if (first.trim() && !/^[a-z]+$/i.test(first.trim())) body.push(first);
            let j = i + 1;
            while (j < lines.length && !lines[j].includes(FENCE)) { body.push(lines[j]); j += 1; }
            if (j < lines.length) {
                const before = lines[j].slice(0, lines[j].indexOf(FENCE));
                if (before.trim()) body.push(before);
            }
            flushMd();
            const table = tableFromRows(body.filter((l) => l.trim() !== ""));
            blocks.push(table || { type: "code", text: body.join("\n").replace(/\s+$/, "") });
            i = j + 1;
            continue;
        }

        // A run of two or more `mono` rows is a table drawn for Telegram.
        if (MONO_ROW.test(line.trim()) && i + 1 < lines.length && MONO_ROW.test(lines[i + 1].trim())) {
            const raw = [];
            const trailing = [];
            let j = i;
            while (j < lines.length) {
                const m = MONO_ROW.exec(lines[j].trim());
                if (!m) break;
                raw.push(m[1]);
                trailing.push(m[2]);
                j += 1;
            }
            const table = tableFromRows(raw, trailing);
            if (table) {
                flushMd();
                blocks.push(table);
                i = j;
                continue;
            }
            // Not tabular after all — fall through and render the lines inline.
        }

        md.push(inlineToMarkdown(line));
        i += 1;
    }
    flushMd();
    return blocks;
}

/** Plain text for notifications / accessibility: markers stripped, tables flattened. */
export function toPlainText(text) {
    return parseTelegram(text)
        .map((b) => {
            if (b.type === "md") return b.lines.join("\n").replace(/\\([_*`\[\]#~>\\])/g, "$1").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1");
            if (b.type === "table") return [b.header, ...b.rows].map((r) => r.join("  ")).join("\n");
            return b.text;
        })
        .join("\n\n");
}
